// useFocusHeartbeat — the per-USER presence heartbeat (Brief 5 pt16).
//
// Reports THIS surface's focus — active / backgrounded / idle — to the backend
// so it knows whether the user is reachable in-app right now. That gates the
// async-steering escalation (pt17): a question the user can SEE in-app waits in
// the thread; a user who is backgrounded/idle/away gets the async reach instead.
//
// Focus is a COMPUTED value, never a hardcoded "active":
//   * backgrounded ⇐ the tab lost visibility (`document.visibilityState`) — the
//     reliable signal (a blur alone, e.g. opening devtools, does not count).
//   * idle         ⇐ visible but no pointer/key input for `IDLE_AFTER_MS`.
//   * active       ⇐ visible and interacted within the idle window.
// The periodic interval re-posts the CURRENT computed focus (so a backgrounded-
// but-alive tab keeps reading backgrounded, not active — otherwise it would
// wrongly suppress a needed reach), and every transition posts immediately.
//
// If the tab is closed or the JS dies, heartbeats simply STOP — the backend's
// freshness window then reads the user as not-reachable (fail toward reaching
// them async). So this hook only has to keep the entry fresh + truthful; the
// safety actually lives in the server's staleness check.
//
// Mounted ONCE for a signed-in session (from `PresenceProvider`, which is the
// SOLE caller — see PresenceContext's single-owner guard). The wire is the
// injected client (`useApi().postFocusHeartbeat`); an explicit `sendImpl`
// overrides it for tests. `computeFocus` is pure + clock-injected so the focus
// decision is unit-tested without fake timers.

import { useEffect, useRef } from "react";
import type { FocusState } from "@waytide/core";
import { useApi } from "../api";

/** No input for this long (while visible) ⇒ idle. Generous: the user is still
 * "around" but away from the keyboard, so a pending question is better reached
 * async than left on a screen they aren't acting on. */
export const IDLE_AFTER_MS = 3 * 60 * 1000;

/** Re-post the current focus at least this often, so a long-lived surface stays
 * fresh in the backend's window even with no transitions. Faster than the
 * server freshness window (30s) so a couple of missed beats stay reachable. */
export const HEARTBEAT_INTERVAL_MS = 15 * 1000;

/** The wire a heartbeat uses — the real client, or a fake in tests. */
export type SendFocus = (focus: FocusState) => Promise<void>;

/** PURE focus decision (clock-injected). Hidden ⇒ backgrounded regardless of
 * input; otherwise idle if the last interaction is older than `idleAfterMs`,
 * else active. Unit-tested without timers. */
export function computeFocus(
  hidden: boolean,
  lastInteraction: number,
  now: number,
  idleAfterMs: number,
): FocusState {
  if (hidden) return "backgrounded";
  if (now - lastInteraction >= idleAfterMs) return "idle";
  return "active";
}

/**
 * Report this surface's user focus to the backend on every transition and on a
 * periodic interval. Fire-and-forget — a failed post is swallowed (the next
 * beat carries the truth; the server treats a gap as not-reachable anyway).
 *
 * @param sendImpl injected wire — defaults to the app-provided client
 *   (`useApi().postFocusHeartbeat`); pass a fake in tests.
 */
export function useFocusHeartbeat(sendImpl?: SendFocus): void {
  const api = useApi();
  const send_ = sendImpl ?? api.postFocusHeartbeat;
  const sendRef = useRef(send_);
  sendRef.current = send_;

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    let lastInteraction = Date.now();
    // The last focus we posted — so the interval and transitions only POST on a
    // real change (plus the interval's periodic keep-alive), never spam.
    let lastPosted: FocusState | null = null;

    const post = (focus: FocusState) => {
      lastPosted = focus;
      // Swallow: a heartbeat is best-effort; never surface an error to the user.
      void sendRef.current(focus).catch(() => {});
    };

    const current = (): FocusState =>
      computeFocus(
        document.visibilityState === "hidden",
        lastInteraction,
        Date.now(),
        IDLE_AFTER_MS,
      );

    // Post the current focus, but only when it changed (the transition path).
    const postIfChanged = () => {
      const focus = current();
      if (focus !== lastPosted) post(focus);
    };

    const onInteract = () => {
      lastInteraction = Date.now();
      // Coming back from idle → active is a transition worth posting at once.
      postIfChanged();
    };
    const onVisibility = () => postIfChanged();

    document.addEventListener("visibilitychange", onVisibility);
    // Passive: we only read the timestamp, never block the gesture.
    window.addEventListener("pointerdown", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract, { passive: true });
    window.addEventListener("focus", onVisibility);

    // The keep-alive: re-post the CURRENT computed focus every interval — this
    // both refreshes the server's freshness window AND flips us to `idle` once
    // the idle threshold passes with no input (no event fires for "went idle").
    const timer = setInterval(() => post(current()), HEARTBEAT_INTERVAL_MS);

    // An immediate first beat so the backend knows we're here without waiting a
    // whole interval.
    post(current());

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("focus", onVisibility);
      clearInterval(timer);
    };
  }, []);
}
